import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiErrorResponse, CancelledApiCall } from '@/services/api'
import { teacherPriorityApi } from '@/services/api/teacherPriority'
import { CalendarView } from '@/components/teacher-request/CalendarView'
import { DayLessonsPanel } from '@/components/teacher-request/DayLessonsPanel'
import { HeaderBar } from '@/components/teacher-request/HeaderBar'
import { ReviewModal } from '@/components/teacher-request/ReviewModal'
import { TeacherPriorityCard } from '@/components/teacher-request/TeacherPriorityCard'
import { StundenplanSchnitt } from '@/components/teacher-request/StundenplanSchnitt'
import { NoTeacherFallback } from '@/components/teacher-request/NoTeacherFallback'
import { PackageList } from '@/components/teacher-request/PackageList'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { mergeMonthDays, useTeacherCalendarMonths } from '@/hooks/useCalendarData'
import { useTeacherRequestConfig } from '@/hooks/useTeacherRequestConfig'
import { useToast } from '@/components/ui/ToastProvider'
import { getDatesInRange, isLessonInPast, monthsInRange, todayLocalISO } from '@/lib/date-utils'
import { formatMatchEta } from '@/utils/matchEta'
import { buildBrockenUnits, brockenSeriesKey, type WeekRankMode } from '@/utils/brockenUnits'
import { AlertTriangle } from 'lucide-react'
import type { AbsenceType, DateRange, SelectionMode } from '@/types/teacher-request'
import { decideSend, headerButtonLabel, type ApiFailure, type AvailabilityLoad, type QualifiedTeacherSearch, type SendEntry, type SendInput } from '@/teacher-request/apiFailure'

interface SelectedLesson {
  entryId: string
  date: string
  timeSlotId: string
  subjectId?: string
  backgroundId?: string
  subjectName?: string
  backgroundName?: string
  klassName?: string
  startTime?: string
  endTime?: string
}

function isSelectableLesson(l: any): boolean {
  return !!l.is_requestable && !isLessonInPast(l.date, l.end_time)
}

function toSelectedLesson(l: any): SelectedLesson {
  return {
    entryId: l.entry_id,
    date: l.date,
    timeSlotId: l.time_slot_id,
    subjectId: l.subject_id,
    backgroundId: l.background_id,
    subjectName: l.subject_name,
    backgroundName: l.background_name,
    klassName: l.klass_name,
    startTime: l.start_time,
    endTime: l.end_time,
  }
}

function collectRequestableLessons(days: any[] | undefined, dates: string[]): SelectedLesson[] {
  const dateSet = new Set(dates)
  const newLessons: SelectedLesson[] = []
  for (const day of days ?? []) {
    if (!dateSet.has(day.date)) continue
    for (const l of day.lessons ?? []) {
      if (!isSelectableLesson(l)) continue
      newLessons.push(toSelectedLesson(l))
    }
  }
  return newLessons
}

interface AvailabilityGroup {
  subjectId: string | null
  backgroundId: string | null
  subjectName: string | null
  backgroundName: string | null
  timeSlotIds: string[]
  teacherIds: string[]
}

interface CoverageBlockDraft {
  id: string
  date?: string
  timeSlotIds: string[]
  blockName: string | null
  blockType: 'full_day' | 'partial'
  blockRank: number
  packageId: string | null
  requestMode: 'sequential' | 'parallel' | null
  customThresholdMinutes: number | null
  backgroundId: string | null
  entries: { teacherId: string; rank: number; teacherName: string; teacherCode?: string; rating: number }[]
}

interface TeacherRequestState {
  viewMode: 'calendar' | 'priorities'
  currentDate: Date
  selectedDate: string
  selectionMode: SelectionMode
  dateRange: DateRange | null
  rangeStep: 'from' | 'to'
  selectedLessons: SelectedLesson[]
  coverageBlocks: CoverageBlockDraft[]
  availabilityGroups: AvailabilityGroup[]
  partialHours: {
    mode: 'manual' | 'automatic'
    selectedSlots: string[]
    packages: { id: string; timeSlots: string[]; priority: number; eligibleTeachers: string[] }[]
    singleHours: { timeSlot: string; priority: number; eligibleTeachers: string[] }[]
    teacherPriorities: { teacherId: string; teacherName: string; teacherCode?: string; slotIds: string[]; rank: number }[]
  }
  requestMode: 'sequential' | 'parallel'
  customThresholdMinutes: number | null
  fallbackMode: 'auto' | 'manual' | null
  cascadeStrategy: 'full_day_first' | 'unfilled_only' | null
  parallelDeadlineMinutes: number | null
  messageToSubstitutes: string
  reason: string
  documents: File[]
  isUrgent: boolean
  absenceType: AbsenceType | ''
  requestStatus: 'draft' | 'submitted' | 'active' | 'director_pending' | 'filled' | 'expired' | 'cancelled'
  liveUpdates: { teacherId: string; status: string; respondedAt?: string; requestId?: string }[]
  undoHistory: { action: string; timestamp: number; data: any }[]
  currentRequestId: string | null
}

function failureBanner(error: unknown): string | null {
  if (error instanceof CancelledApiCall) return null
  if (error instanceof ApiErrorResponse) return error.detail || null
  return null
}

function apiFailureFromError(error: unknown, method: string, address: string): ApiFailure {
  if (error instanceof CancelledApiCall) {
    return error.failure ?? { method, address, status: null, code: 'ERR_CANCELED', body: null }
  }
  if (error instanceof ApiErrorResponse && error.failure) return error.failure
  if (error instanceof ApiErrorResponse) {
    const status = error.statusCode > 0 ? error.statusCode : null
    return {
      method,
      address,
      status,
      code: status == null ? 'ERR_NETWORK' : null,
      body: error.detail ? { detail: error.detail } : null,
    }
  }
  return { method, address, status: null, code: 'ERR_NETWORK', body: null }
}

export function TeacherRequestPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const [state, setState] = useState<TeacherRequestState>({
    viewMode: 'calendar',
    currentDate: new Date(),
    selectedDate: todayLocalISO(),
    selectionMode: 'single',
    dateRange: null,
    rangeStep: 'from',
    selectedLessons: [],
    coverageBlocks: [],
    availabilityGroups: [],
    partialHours: {
      mode: 'manual',
      selectedSlots: [],
      packages: [],
      singleHours: [],
      teacherPriorities: [],
    },
    requestMode: 'sequential',
    customThresholdMinutes: null,
    fallbackMode: null as 'auto' | 'manual' | null,
    cascadeStrategy: null as 'full_day_first' | 'unfilled_only' | null,
    parallelDeadlineMinutes: null,
    messageToSubstitutes: '',
    reason: '',
    documents: [],
    isUrgent: false,
    absenceType: '',
    requestStatus: 'draft',
    liveUpdates: [],
    undoHistory: [],
    currentRequestId: null,
  })
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'full_day' | 'partial'>('full_day')
  const [weekRankMode, setWeekRankMode] = useState<WeekRankMode>('repeat')
  const [matrixFocusDate, setMatrixFocusDate] = useState<string | null>(null)
  const [matrixLayout, setMatrixLayout] = useState<'m1' | 'm2' | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [dismissedBlock, setDismissedBlock] = useState<string | null>(null)
  const [, setIsCreatingPackage] = useState(false)
  const [, setIsGeneratingAutoPackages] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [forwardDecision, setForwardDecision] = useState<{ requestId: string; count: number } | null>(null)
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null)
  const [requestFlow, setRequestFlow] = useState<'prioritized' | 'automatic'>('automatic')
  const [qualifiedTeacherSearch, setQualifiedTeacherSearch] = useState<QualifiedTeacherSearch>({ status: 'absent' })
  const [isUndoing, setIsUndoing] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const hasInitializedMode = useRef(false)
  const pendingDefaultLessonSelect = useRef(false)
  const userEditedSelection = useRef(false)
  const prevPriorityOrderKey = useRef('')
  const [availabilityData, setAvailabilityData] = useState<any>(null)
  const [availabilityLoad, setAvailabilityLoad] = useState<AvailabilityLoad>({ status: 'not_needed' })
  const isSearchingTeachers = qualifiedTeacherSearch.status === 'loading'
  const qualifiedTeachersCount = qualifiedTeacherSearch.status === 'ready' ? qualifiedTeacherSearch.count : 0
  const isAvailabilityLoading = availabilityLoad.status === 'loading'

  const calendarYear = state.currentDate.getFullYear()
  const calendarMonth = state.currentDate.getMonth() + 1

  const monthsToLoad = useMemo(() => {
    const months = [{ year: calendarYear, month: calendarMonth }]
    if (state.dateRange?.from && state.dateRange?.to) {
      for (const m of monthsInRange(state.dateRange.from, state.dateRange.to)) {
        if (!months.some(x => x.year === m.year && x.month === m.month)) months.push(m)
      }
    }
    return months
  }, [calendarYear, calendarMonth, state.dateRange])

  const monthQueries = useTeacherCalendarMonths(monthsToLoad)
  const calendarDays = mergeMonthDays<{ date: string; lessons: any[]; is_school_day?: boolean }>(
    monthQueries.map(q => q.data),
  )
  const calendarData = monthQueries[0]?.data
  const calendarLoading = !calendarData && (monthQueries[0]?.isLoading ?? true)
  const rangeMonthsReady = monthQueries.every(q => q.isSuccess || q.isError)
  const { data: calendarConfig } = useQuery({
    queryKey: ['calendar-config'],
    queryFn: () => api.get('/api/v1/teacher-requests/calendar-config'),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: state.viewMode === 'calendar',
  })
  const {
    mySettings,
    requestConfig,
    requestConfigLoading,
    requestConfigError,
    refetchMySettings,
  } = useTeacherRequestConfig()

  // D1/E1: Direktor-Gate-Settings für die Modal-Öffnung (kurze staleTime +
  // refetchOnWindowFocus → E13-Umschaltung wird zeitnah übernommen, EC8).
  const { data: directorGateSettings } = useQuery({
    queryKey: ['director-gate-settings'],
    queryFn: () => teacherPriorityApi.getDirectorGateSettings(),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  })
  const directorEnabled = directorGateSettings?.director_enabled === true

  const selectedTimeSlotIds = useMemo(() => 
    state.selectedLessons.map(l => l.timeSlotId),
    [state.selectedLessons]
  )

  const selectedDates = useMemo(() => {
    if (state.selectionMode === 'range' && state.dateRange) {
      return getDatesInRange(state.dateRange.from, state.dateRange.to)
    }
    return state.selectedDate ? [state.selectedDate] : []
  }, [state.selectionMode, state.dateRange, state.selectedDate])

  useEffect(() => {
    if (!pendingDefaultLessonSelect.current || !rangeMonthsReady) return
    pendingDefaultLessonSelect.current = false
    if (userEditedSelection.current) return
    const lessons = collectRequestableLessons(calendarDays, selectedDates)
    setState(prev => ({ ...prev, selectedLessons: lessons }))
  }, [calendarDays, selectedDates, rangeMonthsReady])

  const createFullDayBlocksFromGroups = useCallback((_groups: AvailabilityGroup[]) => {
    setState(prev => {
      const dates = [...new Set(prev.selectedLessons.map(l => l.date).filter(Boolean))].sort()
      const existingByDate = new Map(
        prev.coverageBlocks
          .filter(b => b.blockType === 'full_day' && b.date)
          .map(b => [b.date as string, b]),
      )
      const blocks: CoverageBlockDraft[] = dates.map((dateStr, idx) => {
        const dayLessons = prev.selectedLessons.filter(l => l.date === dateStr)
        const slots = Array.from(new Set(dayLessons.map(l => l.timeSlotId).filter(Boolean)))
        const dateLabel = new Date(`${dateStr}T00:00:00`).toLocaleDateString('de-DE', {
          weekday: 'long',
          day: '2-digit',
          month: '2-digit',
        })
        const prevBlock = existingByDate.get(dateStr)
        return {
          id: prevBlock?.id || `block-full-day-${dateStr}`,
          date: dateStr,
          timeSlotIds: slots,
          blockName: `Ganztag (${dateLabel})`,
          blockType: 'full_day' as const,
          blockRank: idx + 1,
          packageId: null,
          requestMode: null,
          customThresholdMinutes: null,
          backgroundId: null,
          entries: prevBlock?.entries || [],
        }
      })
      const partials = prev.coverageBlocks.filter(b => b.blockType === 'partial')
      return { ...prev, coverageBlocks: [...blocks, ...partials] }
    })
  }, [])

  useEffect(() => {
    if (requestFlow !== 'automatic') return
    if (state.selectedLessons.length === 0) {
      setQualifiedTeacherSearch({ status: 'absent' })
      return
    }

    let cancelled = false
    setQualifiedTeacherSearch({ status: 'loading' })

    const timeoutId = setTimeout(async () => {
      const allEntryIds = state.selectedLessons.map(l => l.entryId)
      // preview-v5: konkrete Absenzdaten mitgeben — Busy-Check-Parität
      // mit dem Dispatch (recurring Entries ohne Datum → sonst "0 Lehrer").
      const dateOpts = state.selectionMode === 'range' && state.dateRange
        ? { dateFrom: state.dateRange.from, dateTo: state.dateRange.to }
        : { date: state.selectedDate }
      try {
        const data = await teacherPriorityApi.getQualifiedTeachersCount(allEntryIds, dateOpts)
        if (!cancelled) setQualifiedTeacherSearch({ status: 'ready', count: data.count })
      } catch (err) {
        if (cancelled) return
        setQualifiedTeacherSearch({
          status: 'failed',
          failure: apiFailureFromError(err, 'GET', '/api/v1/teacher-requests/qualified-teachers-count'),
        })
      }
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [requestFlow, state.selectedLessons, state.selectedDate, state.selectionMode, state.dateRange])

  useEffect(() => {
    if (state.viewMode !== 'priorities') {
      setAvailabilityLoad({ status: 'not_needed' })
      return
    }
    if (state.selectedLessons.length === 0) {
      setAvailabilityData(null)
      setAvailabilityLoad({ status: 'not_needed' })
      return
    }

    let cancelled = false
    setAvailabilityLoad({ status: 'loading' })

    // Debounce API call to prevent race conditions
    const timeoutId = setTimeout(async () => {
      const entryIdsByDate = state.selectedLessons.reduce((acc, lesson) => {
        if (!acc[lesson.date]) acc[lesson.date] = []
        acc[lesson.date]!.push(lesson.entryId)
        return acc
      }, {} as Record<string, string[]>)
      const uniqueDates = Object.keys(entryIdsByDate).sort()

      try {
        let data
        if (uniqueDates.length === 1) {
          const day = uniqueDates[0]!
          const entryIds = entryIdsByDate[day] || []
          data = await teacherPriorityApi.getAvailability(
            day,
            entryIds.length > 0 ? entryIds : undefined
          )
        } else {
          // Range from actual lesson dates. Span limit is Superadmin
          // max_days_per_request (backend), not a hardcoded frontend cap.
          data = await teacherPriorityApi.getAvailabilityForDates(
            uniqueDates,
            entryIdsByDate,
          )
        }
        if (!cancelled) {
          const d = data as any
          // noscore-v3 (N2): no_data_hints auch im Date-Range-Modus sammeln
          // (je Tag) und top-level bereitstellen, damit sie angezeigt werden.
          const collectedHints: string[] = Array.isArray(d?.no_data_hints)
            ? [...d.no_data_hints]
            : []
          if (d?.days) {
            for (const day of Object.values(d.days) as any[]) {
              collectedHints.push(...(day?.no_data_hints || []))
            }
          }
          if (collectedHints.length > 0) {
            d.no_data_hints = [...new Set(collectedHints)]
          }
          setAvailabilityData(data)
          
          // Extract groups from response
          const groups: AvailabilityGroup[] = (d?.groups || []).map((g: any) => ({
            subjectId: g.subject_id || null,
            backgroundId: g.background_id || null,
            subjectName: g.subject_name || null,
            backgroundName: g.background_name || null,
            timeSlotIds: g.time_slot_ids || [],
            teacherIds: g.teacher_ids || [],
          }))
          setState(prev => ({ ...prev, availabilityGroups: groups }))
          
          try {
            createFullDayBlocksFromGroups(groups)
          } catch (error) {
            console.error('[Full-Day] Failed to create blocks:', error)
            showToast({
              type: 'error',
              message: 'Ganztag-Blöcke konnten nicht erstellt werden. Bitte Seite neu laden.'
            })
          }
          
          setAvailabilityLoad({ status: 'ready' })
        }
      } catch (err) {
        if (!cancelled) {
          setAvailabilityLoad({
            status: 'failed',
            failure: apiFailureFromError(err, 'GET', '/api/v1/teacher-requests/availability'),
          })
          setAvailabilityData(null)
          setState(prev => ({ ...prev, availabilityGroups: [] }))
        }
      }
    }, 300) // 300ms debounce

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [state.viewMode, state.selectedLessons, createFullDayBlocksFromGroups])

  useEffect(() => {
    if (!requestConfig) return
    if (state.viewMode !== 'priorities' || activeTab !== 'partial') return
    const keepTogether = requestConfig.keep_double_lessons_together !== 'off'
    setState(prev => {
      const units = buildBrockenUnits(prev.selectedLessons, { keepTogether })
      const full = prev.coverageBlocks.filter(b => b.blockType === 'full_day')
      const existing = prev.coverageBlocks.filter(b => b.blockType === 'partial')
      const nextPartial = units.map((u, idx) => {
        const id = `block-brocken-${u.date}-${u.fingerprint}`
        const prevB = existing.find(b => b.id === id)
          || existing.find(b =>
            b.date === u.date
            && [...b.timeSlotIds].sort().join(',') === u.fingerprint,
          )
        return {
          id,
          date: u.date,
          timeSlotIds: u.timeSlotIds,
          blockName: u.label,
          blockType: 'partial' as const,
          blockRank: idx + 1,
          packageId: null,
          requestMode: null,
          customThresholdMinutes: null,
          backgroundId: null,
          entries: prevB?.entries || [],
        }
      })
      const same = nextPartial.length === existing.length
        && nextPartial.every((b, i) => b.id === existing[i]?.id && b.timeSlotIds.join() === existing[i]?.timeSlotIds.join())
      if (same) return prev
      return { ...prev, coverageBlocks: [...full, ...nextPartial] }
    })
  }, [activeTab, state.viewMode, state.selectedLessons, requestConfig])

  useEffect(() => {
    setState(prev => ({ ...prev, availabilityGroups: [] }))
  }, [state.selectedLessons])

  const allTeachers = (availabilityData?.teachers?.length
    ? availabilityData.teachers
    : Object.values((availabilityData as any)?.days || {}).flatMap((day: any) => day?.teachers || [])) as any[]

  const allTimeSlots = availabilityData?.time_slots?.length
    ? availabilityData.time_slots
    : Object.values((availabilityData as any)?.days || {}).flatMap((day: any) => day?.time_slots || [])

  const _timeSlots = selectedTimeSlotIds.length > 0
    ? allTimeSlots.filter((ts: any) => selectedTimeSlotIds.includes(ts.id))
    : allTimeSlots
  void _timeSlots

  const priorityOrderKey = requestConfig?.default_mode_priority_order?.join(',') || ''


  useEffect(() => {
    if (!requestConfig) return
    
    const priorityOrderChanged = prevPriorityOrderKey.current !== priorityOrderKey
    if (priorityOrderChanged) {
      prevPriorityOrderKey.current = priorityOrderKey
      hasInitializedMode.current = false
    }
    
    if (hasInitializedMode.current) return
    
    const defaultMode = requestConfig?.default_request_mode
    const priorityOrder = (requestConfig?.default_mode_priority_order || []).filter(
      (m: string) => m === 'automatic' || m === 'priority',
    )

    const applyMode = (mode: string | undefined) => {
      if (mode === 'automatic' && requestConfig?.allow_automatic_mode) {
        setRequestFlow('automatic')
        return true
      }
      if (mode === 'priority' || mode === 'parallel' || mode === 'sequential') {
        setRequestFlow('prioritized')
        return true
      }
      return false
    }

    if (!applyMode(defaultMode)) {
      for (const mode of priorityOrder) {
        if (applyMode(mode)) break
      }
    }
    hasInitializedMode.current = true
  }, [requestConfig, priorityOrderKey])

  const createMutation = useMutation({
    mutationFn: (data: any) => teacherPriorityApi.createRequest(data),
    onSuccess: (result: any) => {
      setShowReviewModal(false)
      if (result?.request_id) {
        queryClient.invalidateQueries({ queryKey: ['teacher-calendar-month'] })
        queryClient.invalidateQueries({ queryKey: ['request-config'] })
        queryClient.invalidateQueries({ queryKey: ['my-settings'] })
      }
    },
    onError: (e: unknown) => {
      setIsSubmitting(false)
      const sentence = failureBanner(e)
      if (!sentence) return
      if (
        sentence.includes('Prioritaetssystem ist deaktiviert')
        || sentence.includes('Prioritätssystem ist deaktiviert')
        || sentence.includes('schrittweise eingefuehrt')
        || sentence.includes('schrittweise eingeführt')
        || sentence.includes('nicht erlaubt')
      ) {
        queryClient.invalidateQueries({ queryKey: ['my-settings'] })
      }
      setSubmitError(sentence)
    },
  })

  const submitMutation = useMutation({
    mutationFn: (vars: { requestId: string }) =>
      teacherPriorityApi.submitRequest(vars.requestId),
    onSuccess: (result, variables) => {
      // R4/B1: effektiver Status aus dem Backend — Gate-Pause wird NICHT als
      // "aktiv" dargestellt und es wird nicht wegnavigiert.
      const paused = result?.request_status === 'director_pending'
      setIsSubmitting(false)
      setState(prev => ({
        ...prev,
        currentRequestId: variables.requestId,
        requestStatus: paused ? 'director_pending' : 'active',
      }))
      // Konzept A — unverdeckte Lektionen im manual-Modus: Fallback anzeigen statt weg-navigieren
      if (result?.needs_forward_decision && result.uncovered_lesson_count) {
        setForwardDecision({ requestId: variables.requestId, count: result.uncovered_lesson_count })
        showToast({
          message: `${result.uncovered_lesson_count} Lektion(en) ohne Vertretungslehrer – bitte an Admin weiterleiten.`,
          type: 'info',
          duration: 6000,
        })
        return
      }
      if (paused) {
        // IM7: echte Backend-Message + Pause-Session statt generischem Toast
        showToast({
          message: result?.message || 'Anfrage pausiert: Direktor-Freigabe ausstehend.',
          type: 'info',
          duration: 7000,
        })
        return
      }
      showToast({ message: result?.message || 'Vertretungsanfrage erfolgreich eingereicht', type: 'success', duration: 5000 })
      setTimeout(() => navigate('/teacher/requests'), 800)
    },
    onError: (e: unknown) => {
      setIsSubmitting(false)
      const sentence = failureBanner(e)
      if (!sentence) return
      setSubmitError(sentence)
    },
  })

  const handleDateSelect = useCallback((date: string) => {
    if (date < todayLocalISO()) return
    setState(prev => {
      const updates: Partial<TeacherRequestState> = {
        selectedDate: date,
        selectedLessons: [],
      }

      if (prev.selectionMode === 'range') {
        if (prev.rangeStep === 'from') {
          updates.dateRange = { from: date, to: date }
          updates.rangeStep = 'to'
        } else {
          let from = prev.dateRange?.from || date
          let to = date
          if (to < from) {
            const tmp = from
            from = to
            to = tmp
          }
          updates.dateRange = { from, to }
          updates.rangeStep = 'from'
          const dates = getDatesInRange(from, to)
          updates.selectedLessons = collectRequestableLessons(calendarDays, dates)
          pendingDefaultLessonSelect.current = true
          userEditedSelection.current = false
        }
      }

      return { ...prev, ...updates }
    })
  }, [calendarDays])

  const handleSelectionModeChange = useCallback((mode: SelectionMode) => {
    setState(prev => {
      const updates: Partial<TeacherRequestState> = { selectionMode: mode }

      if (mode === 'single') {
        updates.dateRange = null
        updates.selectedLessons = []
        updates.rangeStep = 'from'
      } else {
        updates.dateRange = { from: prev.selectedDate, to: prev.selectedDate }
        updates.rangeStep = 'from'
        updates.selectedLessons = collectRequestableLessons(calendarDays, [prev.selectedDate])
        pendingDefaultLessonSelect.current = true
        userEditedSelection.current = false
      }

      return { ...prev, ...updates }
    })
  }, [calendarDays])

  const handleDateRangeUpdate = useCallback((range: DateRange | null) => {
    const today = todayLocalISO()
    if (range && (range.from < today || range.to < today)) return
    setState(prev => ({
      ...prev,
      dateRange: range,
      selectedDate: range?.from || prev.selectedDate,
      rangeStep: range ? 'from' : 'from',
    }))
  }, [])

  const handleDateRangeCommit = useCallback((range: DateRange | null) => {
    const today = todayLocalISO()
    if (range && (range.from < today || range.to < today)) return
    const dates = range ? getDatesInRange(range.from, range.to) : []
    const lessons = range ? collectRequestableLessons(calendarDays, dates) : []
    pendingDefaultLessonSelect.current = !!range
    userEditedSelection.current = false
    setState(prev => ({
      ...prev,
      dateRange: range,
      selectedLessons: lessons,
      selectedDate: range?.from || prev.selectedDate,
      rangeStep: range ? 'from' : 'from',
      undoHistory: [...prev.undoHistory, {
        action: 'date_range_change',
        timestamp: Date.now(),
        data: { previousRange: prev.dateRange, newRange: range },
      }],
    }))
  }, [calendarDays])

  const handleLessonToggle = useCallback((lesson: any) => {
    userEditedSelection.current = true
    setState(prev => {
      const exists = prev.selectedLessons.find(
        l => l.entryId === lesson.entry_id && l.date === lesson.date && (l.startTime || '') === (lesson.start_time || ''),
      )
      let newLessons: SelectedLesson[]
      if (exists) {
        newLessons = prev.selectedLessons.filter(
          l => !(l.entryId === lesson.entry_id && l.date === lesson.date && (l.startTime || '') === (lesson.start_time || '')),
        )
      } else {
        if (isLessonInPast(lesson.date, lesson.end_time)) return prev
        newLessons = [...prev.selectedLessons, toSelectedLesson(lesson)]
      }
      return { ...prev, selectedLessons: newLessons }
    })
  }, [])

  const handleSelectAllLessons = useCallback(() => {
    userEditedSelection.current = true
    const datesToProcess = selectedDates.length > 0 ? selectedDates : [state.selectedDate]
    const newLessons: SelectedLesson[] = []

    for (const date of datesToProcess) {
      const dayData = calendarDays.find((d: any) => d.date === date)
      if (dayData) {
        for (const l of dayData.lessons) {
          if (isSelectableLesson(l)) {
            newLessons.push(toSelectedLesson(l))
          }
        }
      }
    }

    setState(prev => ({ ...prev, selectedLessons: newLessons }))
  }, [calendarDays, state.selectedDate, selectedDates])

  const handleDeselectAllLessons = useCallback(() => {
    userEditedSelection.current = true
    setState(prev => ({ ...prev, selectedLessons: [] }))
  }, [])

  const handleSelectDay = useCallback((date: string) => {
    userEditedSelection.current = true
    const dayData = calendarDays.find((d: any) => d.date === date)
    if (!dayData) return

    const newLessonsForDay: SelectedLesson[] = dayData.lessons
      .filter((l: any) => isSelectableLesson(l))
      .map((l: any) => toSelectedLesson(l))

    setState(prev => {
      const existingOtherDays = prev.selectedLessons.filter(l => l.date !== date)
      return { ...prev, selectedLessons: [...existingOtherDays, ...newLessonsForDay] }
    })
  }, [calendarDays])

  const handleDeselectDay = useCallback((date: string) => {
    userEditedSelection.current = true
    setState(prev => ({
      ...prev,
      selectedLessons: prev.selectedLessons.filter(l => l.date !== date),
    }))
  }, [])

  const handleBackToCalendar = useCallback(() => {
    setState(prev => ({ ...prev, viewMode: 'calendar' }))
  }, [])

  const handleRequestFlowChange = useCallback((flow: 'prioritized' | 'automatic') => {
    setRequestFlow(flow)
    if (flow === 'automatic') {
      setState(prev => prev.viewMode === 'calendar' ? prev : { ...prev, viewMode: 'calendar' })
    }
  }, [])

  const checkDuplicateTeacher = (teacherId: string, blockId: string): string | null => {
    const inOtherBlocks = state.coverageBlocks
      .filter(b => b.id !== blockId)
      .find(b => b.entries.some(e => e.teacherId === teacherId))
    if (inOtherBlocks) {
      const teacher = allTeachers.find((t: any) => t.id === teacherId)
      const name = teacher?.full_name || 'Unbekannter Lehrer'
      return `${name} ist bereits in einem anderen Block ausgewahlt.`
    }
    return null
  }

  const getOrCreatePartialBlock = (slotIds: string[]): CoverageBlockDraft => {
    const normalized = [...slotIds].sort()
    const existing = state.coverageBlocks.find(
      b => b.blockType === 'partial' && [...b.timeSlotIds].sort().join(',') === normalized.join(',')
    )
    if (existing) return existing

    const newBlock: CoverageBlockDraft = {
      id: `block-${Date.now()}`,
      timeSlotIds: normalized,
      blockName: null,
      blockType: 'partial',
      blockRank: state.coverageBlocks.length + 1,
      packageId: null,
      requestMode: null,
      customThresholdMinutes: null,
      backgroundId: null,
      entries: [],
    }
    setState(prev => ({ ...prev, coverageBlocks: [...prev.coverageBlocks, newBlock] }))
    return newBlock
  }

  /**
   * Toggle teacher priority in full-day mode.
   * 
   * If teacher is already in any full-day block: Remove from all blocks (deselection).
   * If teacher is not in any block: Add to all qualified blocks (selection).
   * 
   * Qualified blocks are determined by:
   * - If block has backgroundId: Teacher must be in the matching availability group
   * - If block has no backgroundId: Teacher must be in a group with matching timeSlotIds
   * 
   * Deselection is always allowed, even if teacher is not qualified (for legacy data).
   * 
   * Undo history tracks per-block whether teacher was added or removed using wasAddedMap.
   * 
   * @param teacher - Teacher object with id, full_name, teacher_code, accept_decline_ratio
   */
  const handleFullDayPriorityToggle = (teacher: any, blockId?: string) => {
    if (isAvailabilityLoading) {
      showToast({ type: 'info', message: 'Lehrer werden geladen, bitte warten...' })
      return
    }

    const teacherExists = allTeachers.some((t: any) => t.id === teacher.id)
    if (!teacherExists) {
      console.warn('[TeacherRequestPage] Teacher not found in allTeachers:', teacher.id)
      showToast({ type: 'error', message: 'Lehrer nicht gefunden. Bitte Seite neu laden.' })
      return
    }

    const weekdayOf = (iso?: string) => {
      if (!iso) return -1
      const [y, m, d] = iso.split('-').map(Number)
      if (y === undefined || m === undefined || d === undefined) return -1
      return new Date(y, m - 1, d).getDay()
    }
    const allFull = state.coverageBlocks.filter(b => b.blockType === 'full_day')
    const targetWd = weekdayOf(allFull.find(b => b.id === blockId)?.date)
    const fullDayBlocks = allFull.filter(
      b => !blockId || weekdayOf(b.date) === targetWd,
    )
    const blocksWithTeacher = fullDayBlocks.filter(b => b.entries.some(e => e.teacherId === teacher.id))

    if (blocksWithTeacher.length > 0) {
      setState(prev => {
        const blocks = [...prev.coverageBlocks]
        const wasAddedMap = new Map<string, boolean>()

        blocksWithTeacher.forEach(block => {
          const blockIndex = blocks.findIndex(b => b.id === block.id)
          if (blockIndex === -1) return

          const updatedBlock = { ...blocks[blockIndex] } as CoverageBlockDraft
          const entries = updatedBlock.entries || []
          const filtered = entries.filter(e => e.teacherId !== teacher.id)
          updatedBlock.entries = filtered.map((e, i) => ({ ...e, rank: i + 1 }))
          wasAddedMap.set(block.id, false)
          blocks[blockIndex] = updatedBlock
        })

        return {
          ...prev,
          coverageBlocks: blocks,
          undoHistory: [...prev.undoHistory, {
            action: 'full_day_toggle',
            timestamp: Date.now(),
            data: {
              teacherId: teacher.id,
              wasAddedMap: Object.fromEntries(wasAddedMap),
            },
          }],
        }
      })
      return
    }

    // Konzept A — Ganztags = EIN Block über alle Fächer. Eine Lehrkraft ist nur dann
    // berechtigt, wenn sie für ALLE Fächer qualifiziert und in ALLEN Lektionen verfügbar ist
    // (wholeDayEligibleTeachers). Da nur diese im UI anklickbar sind, ist dies eine Safety-Prüfung.
    const qualifiedBlocks = fullDayBlocks

    if (qualifiedBlocks.length === 0) {
      showToast({ type: 'info', message: 'Lehrkraft ist nicht für alle Fächer qualifiziert oder nicht in allen Lektionen verfügbar.' })
      return
    }

    setState(prev => {
      const blocks = [...prev.coverageBlocks]
      const wasAddedMap = new Map<string, boolean>()

      qualifiedBlocks.forEach(qualifiedBlock => {
        const blockIndex = blocks.findIndex(b => b.id === qualifiedBlock.id)
        if (blockIndex === -1) return

        const block = { ...blocks[blockIndex] } as CoverageBlockDraft
        const entries = block.entries || []
        const newRank = entries.length + 1
        block.entries = [...entries, {
          teacherId: teacher.id,
          rank: newRank,
          teacherName: teacher.full_name,
          teacherCode: teacher.teacher_code,
          rating: teacher.accept_decline_ratio,
        }]
        wasAddedMap.set(qualifiedBlock.id, true)
        blocks[blockIndex] = block
      })

      return {
        ...prev,
        coverageBlocks: blocks,
        undoHistory: [...prev.undoHistory, {
          action: 'full_day_toggle',
          timestamp: Date.now(),
          data: { 
            teacherId: teacher.id,
            wasAddedMap: Object.fromEntries(wasAddedMap),
          },
        }],
      }
    })
  }

  const handleBrockenPriorityToggle = (teacher: any, seriesKey: string) => {
    if (isAvailabilityLoading) {
      showToast({ type: 'info', message: 'Lehrer werden geladen, bitte warten...' })
      return
    }
    const teacherExists = allTeachers.some((t: any) => t.id === teacher.id)
    if (!teacherExists) {
      showToast({ type: 'error', message: 'Lehrer nicht gefunden. Bitte Seite neu laden.' })
      return
    }
    setState(prev => {
      const keepTogether = requestConfig?.keep_double_lessons_together !== 'off'
      const units = buildBrockenUnits(prev.selectedLessons, { keepTogether })
      const targetIds = new Set(
        prev.coverageBlocks
          .filter(b => b.blockType === 'partial' && b.date)
          .filter(b => {
            const u = units.find(x =>
              x.date === b.date
              && [...x.timeSlotIds].sort().join(',') === [...b.timeSlotIds].sort().join(','),
            )
            return u ? brockenSeriesKey(u, weekRankMode) === seriesKey : false
          })
          .map(b => b.id),
      )
      if (targetIds.size === 0) return prev
      const blocks = prev.coverageBlocks.map(block => {
        if (!targetIds.has(block.id)) return block
        const has = block.entries.some(e => e.teacherId === teacher.id)
        if (has) {
          return {
            ...block,
            entries: block.entries
              .filter(e => e.teacherId !== teacher.id)
              .map((e, i) => ({ ...e, rank: i + 1 })),
          }
        }
        return {
          ...block,
          entries: [...block.entries, {
            teacherId: teacher.id,
            rank: block.entries.length + 1,
            teacherName: teacher.full_name,
            teacherCode: teacher.teacher_code,
            rating: teacher.accept_decline_ratio,
          }],
        }
      })
      return { ...prev, coverageBlocks: blocks }
    })
  }

  const handleWeekRankModeChange = (mode: WeekRankMode) => {
    setWeekRankMode(mode)
    if (mode !== 'repeat') return
    setState(prev => {
      const keepTogether = requestConfig?.keep_double_lessons_together !== 'off'
      const units = buildBrockenUnits(prev.selectedLessons, { keepTogether })
      const source = new Map<string, typeof prev.coverageBlocks[number]>()
      for (const b of prev.coverageBlocks) {
        if (b.blockType !== 'partial' || !b.date) continue
        const u = units.find(x =>
          x.date === b.date
          && [...x.timeSlotIds].sort().join(',') === [...b.timeSlotIds].sort().join(','),
        )
        if (!u) continue
        const key = brockenSeriesKey(u, 'repeat')
        const prevB = source.get(key)
        if (!prevB || (b.date < (prevB.date || ''))) source.set(key, b)
      }
      return {
        ...prev,
        coverageBlocks: prev.coverageBlocks.map(b => {
          if (b.blockType !== 'partial' || !b.date) return b
          const u = units.find(x =>
            x.date === b.date
            && [...x.timeSlotIds].sort().join(',') === [...b.timeSlotIds].sort().join(','),
          )
          if (!u) return b
          const src = source.get(brockenSeriesKey(u, 'repeat'))
          if (!src || src.id === b.id) return b
          return { ...b, entries: src.entries.map(e => ({ ...e })) }
        }),
      }
    })
  }

  const _handlePartialSlotToggle = (slotId: string) => {
    setState(prev => {
      const isSelected = prev.partialHours.selectedSlots.includes(slotId)
      const newSlots = isSelected
        ? prev.partialHours.selectedSlots.filter(s => s !== slotId)
        : [...prev.partialHours.selectedSlots, slotId]

      return {
        ...prev,
        partialHours: { ...prev.partialHours, selectedSlots: newSlots },
      }
    })
  }

  const _handlePartialPriorityToggle = (teacherId: string) => {
    if (state.partialHours.selectedSlots.length === 0) return

    if (isAvailabilityLoading) {
      showToast({ type: 'info', message: 'Lehrer werden geladen, bitte warten...' })
      return
    }

    const partialBlock = getOrCreatePartialBlock(state.partialHours.selectedSlots)
    const warning = checkDuplicateTeacher(teacherId, partialBlock.id)
    if (warning && !partialBlock.entries.find(e => e.teacherId === teacherId)) {
      setDuplicateWarning(warning)
      setTimeout(() => setDuplicateWarning(null), 4000)
    }

    setState(prev => {
      const blocks = [...prev.coverageBlocks]
      const blockIndex = blocks.findIndex(b => b.id === partialBlock.id)
      if (blockIndex === -1) return prev

      const block = { ...blocks[blockIndex] } as CoverageBlockDraft
      const entries = block.entries || []
      const existing = entries.find(e => e.teacherId === teacherId)
      let wasAdded = false

      if (existing) {
        const filtered = entries.filter(e => e.teacherId !== teacherId)
        block.entries = filtered.map((e, i) => ({ ...e, rank: i + 1 }))
        wasAdded = false
      } else {
        const teacher = allTeachers.find((t: any) => t.id === teacherId)
        if (!teacher) {
          console.warn('[TeacherRequestPage] Teacher not found in allTeachers:', teacherId)
          showToast({ type: 'error', message: 'Lehrer nicht gefunden. Bitte Seite neu laden.' })
          return prev
        }
        const newRank = entries.length + 1
        block.entries = [...entries, {
          teacherId,
          rank: newRank,
          teacherName: teacher.full_name,
          teacherCode: teacher.teacher_code,
          rating: teacher.accept_decline_ratio || 0,
        }]
        wasAdded = true
      }

      blocks[blockIndex] = block
      return {
        ...prev,
        coverageBlocks: blocks,
        undoHistory: [...prev.undoHistory, {
          action: 'partial_priority_toggle',
          timestamp: Date.now(),
          data: { teacherId, wasAdded },
        }],
      }
    })
  }

  const _handleCreatePackage = async () => {
    if (state.partialHours.selectedSlots.length === 0) return

    if (state.currentRequestId) {
      setIsCreatingPackage(true)
      try {
        const result = await teacherPriorityApi.createPackage(state.currentRequestId, {
          date: state.selectedDate,
          time_slot_ids: state.partialHours.selectedSlots,
          package_rank: state.partialHours.packages.length + 1,
        })
        setState(prev => ({
          ...prev,
          partialHours: {
            ...prev.partialHours,
            packages: [...prev.partialHours.packages, {
              id: result.package_id,
              timeSlots: state.partialHours.selectedSlots,
              priority: prev.partialHours.packages.length + 1,
              eligibleTeachers: [],
            }],
            selectedSlots: [],
          },
        }))
      } catch (e: any) {
        setSubmitError(e?.detail || 'Paket konnte nicht erstellt werden')
      } finally {
        setIsCreatingPackage(false)
      }
    } else {
      const newPackage = {
        id: `pkg-${Date.now()}`,
        timeSlots: [...state.partialHours.selectedSlots],
        priority: state.partialHours.packages.length + 1,
        eligibleTeachers: [],
      }

      setState(prev => ({
        ...prev,
        partialHours: {
          ...prev.partialHours,
          packages: [...prev.partialHours.packages, newPackage],
          selectedSlots: [],
        },
      }))
    }
  }

  const _handleDeletePackage = async (packageId: string) => {
    if (!state.currentRequestId) {
      setState(prev => ({
        ...prev,
        partialHours: {
          ...prev.partialHours,
          packages: prev.partialHours.packages.filter(p => p.id !== packageId),
        },
      }))
      return
    }

    try {
      await teacherPriorityApi.deletePackage(state.currentRequestId, packageId)
      setState(prev => ({
        ...prev,
        partialHours: {
          ...prev.partialHours,
          packages: prev.partialHours.packages.filter(p => p.id !== packageId),
        },
      }))
    } catch (e: any) {
      setSubmitError(e?.detail || 'Paket konnte nicht geloscht werden')
    }
  }

  const _handleAssignPackage = async (packageId: string, teacherId: string) => {
    if (!state.currentRequestId) return

    try {
      const res = await teacherPriorityApi.assignPackage(state.currentRequestId, packageId, teacherId)
      // noscore-v3 (N3): Override-Warnung des Backends sichtbar machen
      // (Zuweisung erfolgte, aber Lehrer ist für Lektionen nicht qualifiziert)
      if (res?.warning) {
        showToast({ type: 'warning', message: res.warning })
      }
      setState(prev => ({
        ...prev,
        partialHours: {
          ...prev.partialHours,
          packages: prev.partialHours.packages.map(p =>
            p.id === packageId ? { ...p, eligibleTeachers: [teacherId] } : p
          ),
        },
      }))
    } catch (e: any) {
      setSubmitError(e?.detail || 'Paket konnte nicht zugewiesen werden')
    }
  }

  void _handlePartialSlotToggle
  void _handlePartialPriorityToggle
  void _handleCreatePackage
  void _handleDeletePackage
  void _handleAssignPackage
  const _handleGenerateAutoPackages = async () => {
    if (!state.currentRequestId) return
    setIsGeneratingAutoPackages(true)
    try {
      await teacherPriorityApi.generateAutoPackages(state.currentRequestId)
      queryClient.invalidateQueries({ queryKey: ['request-status', state.currentRequestId] })
    } catch (e: any) {
      setSubmitError(e?.detail || 'Automatische Pakete konnten nicht generiert werden')
    } finally {
      setIsGeneratingAutoPackages(false)
    }
  }
  void _handleGenerateAutoPackages

  // Konzept A — Senden-Sperre: ≥1 Priorität im Prioritäts-Modus (Entries, nicht Blöcke)
  const totalPriorities = state.coverageBlocks
    .filter(b => activeTab === 'partial' ? b.blockType === 'partial' : b.blockType === 'full_day')
    .reduce((n, b) => n + (b.entries?.length || 0), 0)
  const canSubmit = requestFlow === 'automatic'
    ? state.selectedLessons.length > 0
    : totalPriorities > 0

  function unqualifiedPriorityNames(): string[] {
    if (requestFlow !== 'prioritized' || state.availabilityGroups.length === 0) return []
    const invalidAssignments: string[] = []
    for (const block of state.coverageBlocks) {
      if (block.blockType === 'full_day') {
        const dayKey = block.date || state.selectedDate
        const dayTeachers = ((availabilityData as any)?.days?.[dayKey]?.teachers
          || allTeachers) as any[]
        const dayIds = new Set(dayTeachers.map((t: any) => t.id))
        for (const entry of block.entries) {
          if (dayIds.size > 0 && !dayIds.has(entry.teacherId) && !invalidAssignments.includes(entry.teacherName)) {
            invalidAssignments.push(entry.teacherName)
          }
        }
        continue
      }
      const matchingGroup = state.availabilityGroups.find(g =>
        g.timeSlotIds.every(slotId => block.timeSlotIds.includes(slotId)) &&
        g.timeSlotIds.length === block.timeSlotIds.length
      )
      for (const entry of block.entries) {
        const isQualified = matchingGroup && matchingGroup.teacherIds.includes(entry.teacherId)
        if (!isQualified && !invalidAssignments.includes(entry.teacherName)) {
          invalidAssignments.push(entry.teacherName)
        }
      }
    }
    return invalidAssignments
  }

  function sendInput(entry: SendEntry): SendInput {
    const lessonCount = state.selectedLessons.length
    const maxLessons = requestConfig?.max_lessons_per_request || 100
    const maxDays = requestConfig?.max_days_per_request || 180
    const today = todayLocalISO()
    let pastDate: 'start' | 'end' | 'single' | null = null
    let endBeforeStart = false
    let tooManyDays: number | null = null
    if (state.selectionMode === 'range' && state.dateRange) {
      if (state.dateRange.to < state.dateRange.from) endBeforeStart = true
      else if (state.dateRange.from < today) pastDate = 'start'
      else if (state.dateRange.to < today) pastDate = 'end'
      else if (selectedDates.length > maxDays) tooManyDays = maxDays
    } else if (state.selectedDate && state.selectedDate < today) {
      pastDate = 'single'
    }
    return {
      entry,
      mode: requestFlow,
      view: state.viewMode,
      directorGate: directorEnabled,
      lessonCount,
      priorityCount: totalPriorities,
      absenceTypeChosen: state.absenceType !== '',
      tooManyLessons: lessonCount > maxLessons ? maxLessons : null,
      tooManyDays,
      pastDate,
      endBeforeStart,
      lessonAlreadyPast: state.selectedLessons.some(l => isLessonInPast(l.date, l.endTime)),
      unqualifiedNames: unqualifiedPriorityNames(),
      qualifiedTeacherSearch,
      availability: availabilityLoad,
    }
  }

  function applySendDecision(entry: SendEntry): void {
    const decision = decideSend(sendInput(entry))
    if (decision.outcome === 'sperren' || decision.outcome === 'warten') {
      setDismissedBlock(null)
      setSubmitError(decision.sentence)
      setIsSubmitting(false)
      return
    }
    if (decision.outcome === 'Review öffnen') {
      setShowReviewModal(true)
      return
    }
    if (decision.outcome === 'nächster Schritt') {
      setState(prev => ({ ...prev, viewMode: 'priorities' }))
      return
    }
    void createRequest()
  }

  function handleHeaderClick(): void {
    applySendDecision('header')
  }

  function handleConfirmSubmit(): void {
    setShowReviewModal(false)
    applySendDecision('confirm')
  }

  async function createRequest(): Promise<void> {
    setSubmitError(null)
    setIsSubmitting(true)

    const entryIds = state.selectedLessons.map(l => l.entryId)

    const coverageBlocksPayload = state.coverageBlocks
      .filter(block => (block.timeSlotIds?.length || 0) > 0)
      .filter(block => activeTab === 'partial' ? block.blockType === 'partial' : block.blockType === 'full_day')
      .map(block => ({
        time_slot_ids: block.timeSlotIds,
        block_name: block.blockName,
        block_type: block.blockType,
      block_rank: block.blockRank,
      package_id: block.packageId && !block.packageId.startsWith('pkg-') ? block.packageId : undefined,
      background_id: block.backgroundId || undefined,
      custom_threshold_minutes: block.customThresholdMinutes || undefined,
      date: block.date || undefined,
      entries: block.entries.length > 0 ? block.entries.map((e, idx) => ({
        teacher_id: e.teacherId,
        priority_rank: idx + 1,
      })) : undefined,
    }))

    const overrideAllowed = requestConfig?.allow_teacher_override_threshold !== false
    const fallbackOverrideAllowed = requestFlow === 'prioritized' && requestConfig?.allow_teacher_override_fallback !== false
    const payload = {
      entry_ids: [...new Set(entryIds)],
      date: state.selectionMode === 'range' && state.dateRange
        ? state.dateRange.from
        : state.selectedDate,
      date_to: state.selectionMode === 'range' && state.dateRange
        ? state.dateRange.to
        : null,
      lessons: state.selectedLessons.map(l => ({
        entry_id: l.entryId,
        date: l.date,
        start_time: l.startTime || undefined,
        end_time: l.endTime || undefined,
      })),
      coverage_blocks: requestFlow === 'prioritized' ? coverageBlocksPayload : [],
      request_flow: requestFlow,
      week_rank_mode: requestFlow === 'prioritized' ? weekRankMode : undefined,
      custom_threshold_minutes: overrideAllowed ? state.customThresholdMinutes : undefined,
      priority_fallback_mode: fallbackOverrideAllowed
        ? (state.fallbackMode ?? requestConfig?.priority_fallback_mode ?? 'auto')
        : undefined,
      parallel_deadline_minutes: overrideAllowed && requestFlow === 'automatic' ? 30 : undefined,
      message_to_substitutes: state.messageToSubstitutes || undefined,
      is_urgent: state.isUrgent,
      reason: directorEnabled ? (state.reason || undefined) : undefined,
      absence_type: directorEnabled ? (state.absenceType || undefined) : undefined,
    }

    createMutation.mutate(payload, {
      onSuccess: async (result) => {
        const requestId = result?.request_id
        if (!requestId) {
          setIsSubmitting(false)
          setSubmitError('Keine Anfrage-ID erhalten')
          return
        }

        try {
          // P3-14/15: hochgeladene Dokumente an die erstellte Anfrage hängen,
          // BEVOR der Submit läuft (Backend prüft E7-Hard-Enforcement).
          // m10: Schlägt ein Upload fehl, wird der Submit NICHT abgebrochen
          // (kein steckengebliebener Draft) — die Datei kann in «Meine Anfragen»
          // nachgereicht werden; das Backend blockiert bei `hard` sonst mit
          // einer klaren 409-Meldung.
          let uploadFailed = false
          let uploadedCount = 0
          if (directorEnabled && state.documents && state.documents.length > 0) {
            for (const f of state.documents) {
              try {
                await teacherPriorityApi.uploadRequestDocument(requestId, f)
                uploadedCount += 1
              } catch (e: any) {
                uploadFailed = true
                console.warn(`Dokument «${f.name}» konnte nicht hochgeladen werden: ${e?.detail || 'Unbekannter Fehler'}`)
              }
            }
            setState(prev => ({ ...prev, documents: [] }))
          }
          if (uploadFailed) {
            showToast({
              type: 'warning',
              message: 'Einige Dokumente konnten nicht hochgeladen werden. Sie können sie in «Meine Anfragen» nachreichen.',
              duration: 7000,
            })
          }
          // m4: Liegt bereits mindestens ein Dokument an der Anfrage, ist der
          // Haken «wird nachgereicht» hinfällig — sonst würde der Submit das
          // Flag wieder setzen (V3-2 wird ausgehebelt) und der Direktor sähe
          // «Nachgereicht» + «Eingegangen» gleichzeitig.
          submitMutation.mutate({ requestId })
        } catch (e) {
          console.error('Error in createMutation onSuccess:', e)
          setSubmitError('Fehler beim Verarbeiten der Anfrage')
          setIsSubmitting(false)
        }
      },
    })
  }

  const handleUndoAfterSubmit = async () => {
    if (!state.currentRequestId || isUndoing) return
    setIsUndoing(true)
    try {
      await teacherPriorityApi.undoLastAction(state.currentRequestId)
      setState(prev => ({
        ...prev,
        requestStatus: 'draft',
        undoHistory: prev.undoHistory.slice(0, -1),
        currentRequestId: null,
      }))
    } catch (e: unknown) {
      const detail = (e as { detail?: string })?.detail || 'Aktion konnte nicht rückgängig gemacht werden'
      alert(detail)
    } finally {
      setIsUndoing(false)
    }
  }

  const handleCancelAfterSubmit = async () => {
    if (!state.currentRequestId || isCancelling) return
    if (!confirm('Anfrage wirklich stornieren?')) return
    setIsCancelling(true)
    try {
      await teacherPriorityApi.cancelRequest(state.currentRequestId)
      setState(prev => ({
        ...prev,
        requestStatus: 'cancelled',
      }))
      queryClient.invalidateQueries({ queryKey: ['teacher-calendar-month'] })
      setTimeout(() => navigate('/teacher/requests'), 1500)
    } catch (e: unknown) {
      const detail = (e as { detail?: string })?.detail || 'Anfrage konnte nicht storniert werden'
      alert(detail)
    } finally {
      setIsCancelling(false)
    }
  }

  const handleUndo = () => {
    if (state.undoHistory.length === 0) return

    const lastAction = state.undoHistory[state.undoHistory.length - 1]
    if (!lastAction) return
    if (Date.now() - lastAction.timestamp > 300000) return

    if (lastAction.action === 'full_day_toggle') {
      const teacherId = lastAction.data?.teacherId
      const wasAddedMap = lastAction.data?.wasAddedMap || {}
      if (!teacherId) return

      setState(prev => {
        const blocks = [...prev.coverageBlocks]
        
        for (const [blockId, wasAdded] of Object.entries(wasAddedMap)) {
          const blockIndex = blocks.findIndex(b => b.id === blockId)
          if (blockIndex === -1) continue
          
          const block = { ...blocks[blockIndex] } as CoverageBlockDraft
          const entries = block.entries || []
          
          if (wasAdded) {
            const filtered = entries.filter(e => e.teacherId !== teacherId)
            block.entries = filtered.map((e, i) => ({ ...e, rank: i + 1 }))
          } else {
            const teacher = allTeachers.find((t: any) => t.id === teacherId)
            if (!teacher) continue
            
            const newRank = entries.length + 1
            block.entries = [...entries, {
              teacherId: teacher.id,
              rank: newRank,
              teacherName: teacher.full_name,
              teacherCode: teacher.teacher_code,
              rating: teacher.accept_decline_ratio,
            }]
          }
          
          blocks[blockIndex] = block
        }
        
        return {
          ...prev,
          coverageBlocks: blocks,
          undoHistory: prev.undoHistory.slice(0, -1),
        }
      })
    } else if (lastAction.action === 'partial_priority_toggle') {
      const teacherId = lastAction.data?.teacherId
      const wasAdded = lastAction.data?.wasAdded ?? true
      if (!teacherId) return
      setState(prev => {
        const blocks = prev.coverageBlocks.map(b => {
          if (b.blockType !== 'partial') return b
          const entries = b.entries || []
          if (wasAdded) {
            const filtered = entries.filter(e => e.teacherId !== teacherId)
            const reRanked = filtered.map((e, i) => ({ ...e, rank: i + 1 }))
            return { ...b, entries: reRanked }
          } else {
            const teacher = allTeachers.find((t: any) => t.id === teacherId)
            if (!teacher) return b
            const newRank = entries.length + 1
            return {
              ...b,
              entries: [...entries, {
                teacherId,
                rank: newRank,
                teacherName: teacher.full_name,
                teacherCode: teacher.teacher_code,
                rating: teacher.accept_decline_ratio || 0,
              }],
            }
          }
        }).filter(b => (b.entries?.length ?? 0) > 0 || b.blockType === 'full_day')
        return {
          ...prev,
          coverageBlocks: blocks,
          undoHistory: prev.undoHistory.slice(0, -1),
        }
      })
    } else if (lastAction.action === 'date_range_change') {
      setState(prev => ({
        ...prev,
        dateRange: lastAction.data?.previousRange || null,
        selectedDate: lastAction.data?.previousRange?.from || prev.selectedDate,
        undoHistory: prev.undoHistory.slice(0, -1),
      }))
    }
  }

  const handlePrevMonth = () => {
    setState(prev => {
      const newDate = new Date(prev.currentDate)
      newDate.setMonth(newDate.getMonth() - 1)
      return { ...prev, currentDate: newDate }
    })
  }

  const handleNextMonth = () => {
    setState(prev => {
      const newDate = new Date(prev.currentDate)
      newDate.setMonth(newDate.getMonth() + 1)
      return { ...prev, currentDate: newDate }
    })
  }

  const handleToday = () => {
    setState(prev => ({ ...prev, currentDate: new Date() }))
  }

  const currentDayData = calendarDays.find((d: any) => d.date === state.selectedDate)

  const multiDayData = useMemo(() => {
    const data: Record<string, any> = {}
    for (const date of selectedDates) {
      const dayData = calendarDays.find((d: any) => d.date === date)
      data[date] = dayData || { date, lessons: [], is_school_day: true }
    }
    return data
  }, [selectedDates, calendarDays])

  if (calendarLoading || requestConfigLoading) {
    return <div className="p-6 text-gray-500">Konfiguration laden...</div>
  }

  if (requestConfigError) {
    // Prüfen ob es ein 404-Fehler ist (Teacher-Profil nicht gefunden)
    const errorWithResponse = requestConfigError as { response?: { status?: number }; status?: number; message?: string }
    const is404Error = errorWithResponse?.response?.status === 404 || 
                       errorWithResponse?.status === 404 ||
                       errorWithResponse?.message?.includes('404')
    
    if (is404Error) {
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-700 font-medium">Teacher-Profil nicht gefunden</p>
            <p className="text-red-600 text-sm mt-2">Kontaktieren Sie den Administrator.</p>
          </div>
        </div>
      )
    }
    
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700 font-medium">Einstellungen konnten nicht geladen werden</p>
          <button onClick={() => refetchMySettings()} className="mt-3 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition">
            Erneut versuchen
          </button>
        </div>
      </div>
    )
  }

  const pendingRequestsBlocked = requestConfig && requestConfig.max_pending_requests_per_teacher != null && requestConfig.current_pending_requests != null && requestConfig.current_pending_requests >= requestConfig.max_pending_requests_per_teacher
  const allModesDisabled = requestConfig && requestConfig.allow_automatic_mode === false && requestConfig.priority_system_enabled === false
  const maxPendingIsZero = requestConfig && requestConfig.max_pending_requests_per_teacher != null && requestConfig.max_pending_requests_per_teacher <= 0
  const featureFlagEligible = mySettings?.feature_flag_eligible ?? true

  // Only block for critical issues (not for priority system being disabled)
  if (pendingRequestsBlocked || allModesDisabled || maxPendingIsZero) {
    return (
      <ErrorBoundary>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            {pendingRequestsBlocked && !maxPendingIsZero && (
              <>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Maximale Anfragen erreicht</h2>
                <p className="text-gray-600 mb-4">
                  Sie haben die maximale Anzahl offener Anfragen erreicht ({requestConfig.max_pending_requests_per_teacher}).
                  Bitte warten Sie bis bestehende Anfragen bearbeitet werden.
                </p>
                <button onClick={() => navigate('/teacher/requests')} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition">
                  Meine Anfragen ansehen
                </button>
              </>
            )}
            {allModesDisabled && !pendingRequestsBlocked && !maxPendingIsZero && (
              <>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Keine Anfragemodi verfuegbar</h2>
                <p className="text-gray-600 mb-4">Fuer Ihre Schule sind keine Anfragemodi aktiviert. Bitte kontaktieren Sie den Administrator.</p>
              </>
            )}
            {maxPendingIsZero && !pendingRequestsBlocked && (
              <>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Anfragen derzeit nicht möglich</h2>
                <p className="text-gray-600 mb-4">Das System ist derzeit so konfiguriert, dass keine neuen Anfragen erstellt werden können. Bitte kontaktieren Sie den Administrator.</p>
              </>
            )}
          </div>
        </div>
      </ErrorBoundary>
    )
  }

  const headerDecision = decideSend(sendInput('header'))
  const blockSentence = headerDecision.outcome === 'sperren' || headerDecision.outcome === 'warten'
    ? headerDecision.sentence
    : null
  if (!blockSentence && dismissedBlock) setDismissedBlock(null)
  const bannerText = submitError ?? (blockSentence && blockSentence !== dismissedBlock ? blockSentence : null)

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">

      <HeaderBar
        selectedDate={state.selectedDate}
        requestConfig={requestConfig}
        onDateChange={(date) => {
          if (date && date < todayLocalISO()) return
          setState(prev => ({ ...prev, selectedDate: date }))
        }}
        onSubmit={handleHeaderClick}
        onUndo={handleUndo}
        canUndo={state.undoHistory.length > 0 && state.requestStatus === 'draft' && Date.now() - (state.undoHistory[state.undoHistory.length - 1]?.timestamp ?? 0) < 300000}
        onBackToCalendar={state.viewMode === 'priorities' ? handleBackToCalendar : undefined}
        onProceedToPriorities={handleHeaderClick}
        primaryLabel={headerButtonLabel(sendInput('header'))}
        selectedLessonsCount={state.selectedLessons.length}
        selectionMode={state.selectionMode}
        onSelectionModeChange={handleSelectionModeChange}
        dateRange={state.dateRange}
        onDateRangeUpdate={handleDateRangeUpdate}
        onDateRangeCommit={handleDateRangeCommit}
        requestFlow={requestFlow}
        onRequestFlowChange={handleRequestFlowChange}
        viewMode={state.viewMode}
        qualifiedTeachersCount={qualifiedTeacherSearch.status === 'ready' ? qualifiedTeacherSearch.count : null}
        isSearchingTeachers={isSearchingTeachers}
        prioritySystemEnabled={requestConfig?.priority_system_enabled}
        featureFlagEligible={featureFlagEligible}
        canSubmit={canSubmit}
      />

      {bannerText && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 whitespace-pre-line">
          {bannerText}
          <button onClick={() => {
            if (submitError && submitError !== blockSentence) {
              setSubmitError(null)
              return
            }
            setSubmitError(null)
            if (blockSentence) setDismissedBlock(blockSentence)
          }} className="ml-2 underline">Schliessen</button>
        </div>
      )}

      {forwardDecision && (
        <div className="mx-6 mt-4">
          <NoTeacherFallback
            requestId={forwardDecision.requestId}
            lessonLabel={`${forwardDecision.count} Lektion(en) ohne Vertretungslehrer`}
            onClose={() => setForwardDecision(null)}
            onForwarded={() => setForwardDecision(null)}
          />
        </div>
      )}

      {requestConfig && state.selectedLessons.length > (requestConfig.max_lessons_per_request || 100) && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Maximale Anzahl Lektionen ueberschritten: {state.selectedLessons.length} / {requestConfig.max_lessons_per_request}
        </div>
      )}

      {requestConfig?.package_optimization_enabled && state.selectedLessons.length > 1 && state.viewMode === 'calendar' && (
        <div className="mx-6 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <AlertTriangle className="inline w-4 h-4 mr-1" />
          Lektionen werden automatisch in Pakete gruppiert. Der beste Lehrer für die maximale Abdeckung wird zuerst kontaktiert.
        </div>
      )}

      {requestConfig && state.selectionMode === 'range' && state.dateRange && selectedDates.length > (requestConfig.max_days_per_request || 180) && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Maximale Anzahl Tage ueberschritten: {selectedDates.length} / {requestConfig.max_days_per_request}
        </div>
      )}

      {duplicateWarning && (
        <div className="mx-6 mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-700">
          {duplicateWarning}
          <button onClick={() => setDuplicateWarning(null)} className="ml-2 underline">Schliessen</button>
        </div>
      )}

      {isSubmitting && (
        <div className="mx-6 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          Anfrage wird erstellt und gesendet...
        </div>
      )}

      {state.viewMode === 'calendar' && (
        <div className="px-6 py-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <CalendarView
              month={calendarMonth}
              year={calendarYear}
              days={calendarData?.days || []}
              selectedDate={state.selectedDate}
              onDateSelect={handleDateSelect}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onToday={handleToday}
              selectionMode={state.selectionMode}
              dateRange={state.dateRange}
              weekendSaturdayEnabled={calendarConfig?.weekend_saturday_enabled ?? false}
              weekendSundayEnabled={calendarConfig?.weekend_sunday_enabled ?? false}
            />
          </div>
          <div>
            <DayLessonsPanel
              date={state.selectedDate}
              lessons={currentDayData?.lessons || []}
              selectedLessons={state.selectedLessons}
              onLessonToggle={handleLessonToggle}
              onSelectAll={handleSelectAllLessons}
              onDeselectAll={handleDeselectAllLessons}
              onSelectDay={handleSelectDay}
              onDeselectDay={handleDeselectDay}
              selectedDates={selectedDates}
              multiDayData={multiDayData}
            />
          </div>
        </div>
      )}

      {state.viewMode === 'priorities' && (
        <div className="px-6 py-4">
          <div className="flex gap-2 mb-4 border-b">
            <button
              type="button"
              onClick={() => setActiveTab('full_day')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'full_day' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Ganztags — Wochentag-Serie
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('partial')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'partial' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Stundenplan-Schnitt
            </button>
          </div>

          {availabilityLoad.status === 'failed' ? null : isAvailabilityLoading ? (
            <div className="p-6 bg-white rounded-lg shadow">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-20 bg-gray-200 rounded"></div>
                <div className="h-20 bg-gray-200 rounded"></div>
              </div>
            </div>
          ) : activeTab === 'full_day' && allTeachers.length === 0 ? (
            <div className="space-y-3">
              <div className="p-6 bg-white rounded-lg shadow text-center">
                <p className="text-gray-700 font-medium">Keine Lehrer gefunden</p>
                <p className="text-gray-500 text-sm mt-2">
                  {allTimeSlots.length === 0
                    ? 'Keine Zeitslots für den ausgewählten Tag vorhanden.'
                    : 'Für diese Lektionen ist aktuell kein Lehrer verfügbar/qualifiziert. Nach der Rangliste übernimmt die Automatik die Suche.'}
                </p>
              </div>
              {/* noscore fixup: fail-closed Diagnose (NO_DATA) auch im
                  Seiten-Leerzustand anzeigen — PartialHoursColumn wird hier
                  nicht gerendert. */}
              {(availabilityData?.no_data_hints || []).length > 0 && (
                <div className="space-y-2">
                  {(availabilityData?.no_data_hints || []).map((hint: string, idx: number) => (
                    <div
                      key={`no-data-hint-${idx}`}
                      className="p-3 bg-orange-50 border border-orange-300 rounded-lg text-sm text-orange-800"
                    >
                      {hint}
                    </div>
                  ))}
                </div>
              )}
              {state.currentRequestId && (
                <NoTeacherFallback
                  requestId={state.currentRequestId}
                  lessonLabel={state.selectedLessons.map(l => l.subjectName).filter(Boolean).join(', ') || undefined}
                />
              )}
            </div>
          ) : activeTab === 'full_day' ? (
            <>
              {(() => {
                const fullDayBlocks = state.coverageBlocks.filter(b => b.blockType === 'full_day')
                if (fullDayBlocks.length === 0) {
                  return (
                    <div className="p-6 bg-white rounded-lg shadow text-center">
                      <p className="text-gray-700 font-medium">Keine Ganztages-Auswahl</p>
                      <p className="text-gray-500 text-sm mt-2">
                        Wählen Sie im Kalender zuerst Lektionen aus. Rest nach der Rangliste sucht die Automatik.
                      </p>
                    </div>
                  )
                }
                const weekdayOf = (iso?: string) => {
                  if (!iso) return -1
                  const [y, m, d] = iso.split('-').map(Number)
                  if (y === undefined || m === undefined || d === undefined) return -1
                  return new Date(y, m - 1, d).getDay()
                }
                const WD = ['Sonntage', 'Montage', 'Dienstage', 'Mittwoche', 'Donnerstage', 'Freitage', 'Samstage']
                const groups: { wd: number, blocks: typeof fullDayBlocks }[] = []
                for (const b of fullDayBlocks) {
                  const wd = weekdayOf(b.date)
                  let g = groups.find(x => x.wd === wd)
                  if (!g) {
                    g = { wd, blocks: [] }
                    groups.push(g)
                  }
                  g.blocks.push(b)
                }
                const stacked = matrixLayout === 'm2' || (matrixLayout !== 'm1' && groups.length <= 2)
                const focus = matrixFocusDate || groups[0]?.blocks[0]?.date
                const focusWd = weekdayOf(focus || undefined)
                const visibleGroups = stacked
                  ? groups
                  : groups.filter(g => g.wd === focusWd)
                return (
                  <div className="space-y-4">
                    {groups.length > 1 && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => setMatrixLayout(stacked ? 'm1' : 'm2')}
                          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                          {stacked ? 'Eine Serie' : 'Alle Wochentage'}
                        </button>
                      </div>
                    )}
                    {!stacked && (
                      <div className="flex flex-wrap gap-2">
                        {groups.map(g => (
                          <button
                            key={g.wd}
                            type="button"
                            onClick={() => setMatrixFocusDate(g.blocks[0]?.date || null)}
                            className={`px-3 py-1.5 rounded-lg text-sm ${
                              g.wd === focusWd
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {WD[g.wd] || 'Serie'}
                          </button>
                        ))}
                      </div>
                    )}
                    {visibleGroups.map(g => {
                      const fullDayBlock = g.blocks[0]
                      if (!fullDayBlock) return null
                      const dayKey = fullDayBlock.date || state.selectedDate
                      const dayPayload = (availabilityData as any)?.days?.[dayKey]
                      const dayTeachers = dayPayload?.teachers || allTeachers
                      const dayGroups = dayPayload?.groups || (availabilityData as any)?.groups || []
                      const slotIds = fullDayBlock.timeSlotIds
                      const eligible = dayTeachers.filter((t: any) => {
                        const groupOk = dayGroups.length === 0 || dayGroups.every((g: any) =>
                          (g.teacher_ids || g.teacherIds || []).includes(t.id)
                        )
                        const slotOk = slotIds.every(s => ((t.available_slot_ids || []) as string[]).includes(s))
                        return groupOk && slotOk
                      })
                      return (
                        <div key={`wd-${g.wd}`} className="border rounded-lg p-4 bg-gray-50">
                          <h3 className="text-lg font-semibold mb-1">
                            {WD[g.wd] || 'Serie'}
                            {g.blocks.length > 1 ? ` (${g.blocks.length} Tage)` : ''}
                          </h3>
                          <p className="text-sm text-gray-500 mb-4">
                            {eligible.length} Lehrkraft{eligible.length !== 1 ? 'e' : ''} kann alle
                            {' '}{slotIds.length} Lektion{slotIds.length !== 1 ? 'en' : ''} übernehmen.
                            Rang gilt für alle {WD[g.wd] || 'Tage'} dieser Anfrage.
                          </p>
                          {eligible.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              Niemand kann diesen Tag ganz übernehmen — die Automatik sucht danach Kombinationen.
                            </p>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {eligible.map((t: any) => {
                                const priority = fullDayBlock.entries.find(e => e.teacherId === t.id)
                                const priorityRank = priority?.rank ?? (priority ? 1 : 0)
                                return (
                                  <TeacherPriorityCard
                                    key={`${fullDayBlock.id}-${t.id}`}
                                    teacher={t}
                                    priorityRank={priorityRank}
                                    onToggle={() => handleFullDayPriorityToggle(t, fullDayBlock.id)}
                                    timeSlotsCount={slotIds.length}
                                    selectedDate={dayKey}
                                    requestMode={state.requestMode}
                                    backgroundName={fullDayBlock.blockName || undefined}
                                  />
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })()}
            </>
          ) : (
            <StundenplanSchnitt
              units={buildBrockenUnits(state.selectedLessons, {
                keepTogether: requestConfig?.keep_double_lessons_together !== 'off',
              })}
              coverageBlocks={state.coverageBlocks.filter(b => b.blockType === 'partial')}
              allTeachers={allTeachers}
              availabilityDays={(availabilityData as any)?.days}
              selectedDate={state.selectedDate}
              onToggleTeacher={handleBrockenPriorityToggle}
              weekRankMode={weekRankMode}
            />
          )}
        </div>
      )}

      {state.requestStatus === 'submitted' && state.currentRequestId && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-6">
          <div className="text-center mb-4">
            <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
              Anfrage erfolgreich erstellt!
            </p>
            <p className="text-xs text-green-600 dark:text-green-400">
              Berechnung läuft im Hintergrund. Suche startet in {formatMatchEta(state.selectedLessons.length)}.
            </p>
          </div>
          <div className="flex gap-2 justify-center flex-wrap">
            <button
              onClick={handleUndoAfterSubmit}
              disabled={isUndoing}
              className="px-4 py-2 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 disabled:opacity-50"
            >
              {isUndoing ? 'Wird rückgängig gemacht...' : 'Rückgängig'}
            </button>
            <button
              onClick={handleCancelAfterSubmit}
              disabled={isCancelling}
              className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {isCancelling ? 'Wird storniert...' : 'Stornieren'}
            </button>
            <button
              onClick={() => navigate('/teacher/requests')}
              className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 font-medium"
            >
              Zu meinen Anfragen →
            </button>
          </div>

          {/* noscore fixup (E4): Paket-Übersicht inkl. «Nothilfe»-Kennzeichnung
              (Stage-3-Eskalation) — PackageList war bisher nirgends verdrahtet. */}
          <div className="mt-6 text-left">
            <PackageList requestId={state.currentRequestId} />
          </div>
        </div>
      )}

      {/* IM7: Pause-Session — Direktor-Freigabe ausstehend (R4) */}
      {directorEnabled && state.requestStatus === 'director_pending' && state.currentRequestId && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 mt-6">
          <div className="text-center mb-4">
            <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center text-2xl mb-2">⏸</div>
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-2">
              Anfrage pausiert — Direktor-Freigabe ausstehend
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Die Antwortfristen starten erst nach der Freigabe durch den Direktor.
              Sie werden per E-Mail informiert. Sie können die Anfrage jederzeit stornieren.
            </p>
          </div>
          <div className="flex gap-2 justify-center flex-wrap">
            <button
              onClick={handleCancelAfterSubmit}
              disabled={isCancelling}
              className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {isCancelling ? 'Wird storniert...' : 'Stornieren'}
            </button>
            <button
              onClick={() => navigate('/teacher/requests')}
              className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 font-medium"
            >
              Zu meinen Anfragen →
            </button>
          </div>
        </div>
      )}

      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        onConfirm={handleConfirmSubmit}
        coverageBlocks={state.coverageBlocks}
        requestMode={state.requestMode}
        customThreshold={state.customThresholdMinutes}
        onCustomThresholdChange={(value) => setState(prev => ({ ...prev, customThresholdMinutes: value }))}
        fallbackMode={state.fallbackMode}
        onFallbackModeChange={(value) => setState(prev => ({ ...prev, fallbackMode: value }))}
        cascadeStrategy={state.cascadeStrategy}
        onCascadeStrategyChange={(value) => setState(prev => ({ ...prev, cascadeStrategy: value }))}
        parallelDeadline={state.parallelDeadlineMinutes}
        onParallelDeadlineChange={(value) => setState(prev => ({ ...prev, parallelDeadlineMinutes: value }))}
        messageToSubstitutes={state.messageToSubstitutes}
        onMessageToSubstitutesChange={(value) => setState(prev => ({ ...prev, messageToSubstitutes: value }))}
        isUrgent={state.isUrgent}
        onIsUrgentChange={(value) => setState(prev => ({ ...prev, isUrgent: value }))}
        absenceType={state.absenceType}
        onAbsenceTypeChange={(value) => setState(prev => ({ ...prev, absenceType: value }))}
        reason={state.reason}
        onReasonChange={(value) => setState(prev => ({ ...prev, reason: value }))}
        documents={state.documents}
        onDocumentsChange={(files) => setState(prev => ({ ...prev, documents: files }))}
        directorEnabled={directorEnabled}
        directorSettings={directorGateSettings}
        selectedLessons={state.selectedLessons}
        selectedDate={state.selectedDate}
        selectionMode={state.selectionMode}
        dateRange={state.dateRange}
        requestFlow={requestFlow}
        priorityTab={activeTab}
        qualifiedTeachersCount={qualifiedTeachersCount}
        requestConfig={requestConfig}
        weekRankMode={weekRankMode}
        onWeekRankModeChange={handleWeekRankModeChange}
      />
      </div>
    </ErrorBoundary>
  )
}
